import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Card, CardContent, Typography, Stack, Chip, IconButton, Tooltip, Switch, FormControlLabel
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import deviceConfigService from '../../../services/deviceConfigService';

/**
 * Live command console — shows the most recent commands we SENT to the device
 * and any replies (RECV) captured from its response topics. Polls every 4s so
 * a SHOW CAN CFG reply appears without a manual refresh.
 */
const timeIST = (t) => t ? new Date(t).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: 'short' }) : '';

const DeviceCommandConsole = ({ deviceId, refreshKey }) => {
    const [rows, setRows] = useState([]);
    const [topic, setTopic] = useState('');
    const [auto, setAuto] = useState(true);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await deviceConfigService.getCommandLog(deviceId, 60);
            if (res.success) {
                setRows(res.data || []);
                setTopic(res.commandTopic || '');
            }
        } catch { /* transient — keep last data */ }
        finally { setLoading(false); }
    }, [deviceId]);

    useEffect(() => { load(); }, [load, refreshKey]);

    useEffect(() => {
        if (!auto) { if (timer.current) clearInterval(timer.current); return; }
        timer.current = setInterval(load, 4000);
        return () => { if (timer.current) clearInterval(timer.current); };
    }, [auto, load]);

    return (
        <Card sx={{ borderRadius: '20px', mb: 3, border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" useFlexGap>
                    <Box>
                        <Typography variant="h6" fontWeight={800}>🖥️ Command Console</Typography>
                        {topic && <Typography variant="caption" color="text.secondary" fontFamily="monospace">{topic}</Typography>}
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FormControlLabel
                            control={<Switch size="small" checked={auto} onChange={e => setAuto(e.target.checked)} />}
                            label={<Typography variant="caption" fontWeight={700}>Auto</Typography>}
                        />
                        <Tooltip title="Refresh now">
                            <IconButton size="small" onClick={load} color="primary"><RefreshIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Box sx={{ bgcolor: '#0b1220', borderRadius: '12px', p: 2, maxHeight: 320, overflow: 'auto', fontFamily: 'monospace' }}>
                    {rows.length === 0 ? (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {loading ? 'Loading…' : 'No commands yet. Send a command and its reply (if any) will appear here.'}
                        </Typography>
                    ) : (
                        <Stack spacing={0.75}>
                            {rows.map(r => {
                                const isSent = r.direction === 'SENT';
                                return (
                                    <Stack key={r.id} direction="row" spacing={1} alignItems="flex-start">
                                        <Typography component="span" sx={{ color: '#475569', fontSize: 11, minWidth: 118, pt: '2px' }}>{timeIST(r.created_at)}</Typography>
                                        <Chip label={isSent ? '▲ SENT' : '▼ RECV'} size="small"
                                            sx={{ height: 18, fontSize: 10, fontWeight: 800, borderRadius: '5px',
                                                bgcolor: isSent ? 'rgba(59,130,246,0.15)' : 'rgba(74,222,128,0.15)',
                                                color: isSent ? '#60a5fa' : '#4ade80' }} />
                                        <Typography component="span" sx={{ color: isSent ? '#e2e8f0' : '#86efac', fontSize: 12.5, wordBreak: 'break-all', flex: 1 }}>
                                            {r.command}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Replies are captured from <code>autopeepal/&lt;imei&gt;/response·resp·cfg·reply·ack</code>. If a reply doesn't show, tell me which topic the firmware uses and I'll lock onto it.
                </Typography>
            </CardContent>
        </Card>
    );
};

export default DeviceCommandConsole;
